import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, Pagination, type Column } from '@/components/ui/DataTable';
import { Banner, ErrorState } from '@/components/ui/Feedback';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { PageHeader } from '@/app/PlaceholderPage';
import { useAuth } from '@/features/auth/AuthContext';
import { Button } from '@/components/ui/Button';
import { ApiError } from '@/lib/api';
import { databaseApi, formatMonthYear, type TbmListItem } from './api';
import { FilterBar } from './FilterBar';
import { SelectionBar } from './SelectionBar';
import { cn } from '@/lib/cn';

/** L-05a — Daftar TBM. */
export function TbmListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  const canDelete = user?.role === 'super_admin';

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['tbm', { q, status, page }],
    queryFn: () => databaseApi.listTbm({ q: q || undefined, status: status || undefined, page }),
  });

  const ids = [...selected];

  // Dihitung ulang di server, bukan dijumlah dari memberCount di layar: baris
  // yang dipilih di halaman lain sudah tidak ada di `data`, dan angkanya bisa
  // basi kalau orang lain menambah anggota sejak daftar ini dimuat.
  const preview = useQuery({
    queryKey: ['tbm', 'delete-preview', ids],
    queryFn: () => databaseApi.previewDeleteTbm(ids),
    enabled: confirming && ids.length > 0,
  });

  const deleteMutation = useMutation({
    mutationFn: () => databaseApi.bulkDeleteTbm(ids),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['tbm'] });
      queryClient.invalidateQueries({ queryKey: ['individuals'] });
      setSelected(new Set());
      setConfirming(false);
      setPage(1);
      setBanner(
        `${res.deleted} TBM dihapus` +
          (res.orphanedIndividuals > 0
            ? `. ${res.orphanedIndividuals} data PELITA SLI tetap tersimpan dan sekarang menunggu dipasangkan ke TBM lain.`
            : '.'),
      );
    },
    onError: (err) => {
      setConfirming(false);
      setBanner(err instanceof ApiError ? err.message : 'Gagal menghapus data.');
    },
  });

  const columns: Column<TbmListItem>[] = [
    {
      key: 'name',
      header: 'Nama TBM',
      render: (r) => (
        <div className="flex items-center gap-3">
          {r.photoKey ? (
            <img
              src={r.photoKey.startsWith('http') ? r.photoKey : `/api/v1/files/${r.photoKey}`}
              alt={r.name}
              className="size-9 rounded-lg object-cover border border-border shadow-xs shrink-0"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                if (e.currentTarget.nextElementSibling) {
                  (e.currentTarget.nextElementSibling as HTMLElement).style.display = 'grid';
                }
              }}
            />
          ) : null}
          <span
            className={cn(
              'grid size-9 shrink-0 place-items-center rounded-lg bg-primary-soft text-sm font-semibold text-primary',
              r.photoKey && 'hidden',
            )}
          >
            📚
          </span>
          <div className="min-w-0">
            <span className="font-medium text-ink block">{r.name}</span>
            <span className="tabular text-xs text-ink-secondary">{r.code}</span>
          </div>
        </div>
      ),
      hideOnCard: true,
    },
    { key: 'region', header: 'Wilayah', render: (r) => r.regionName ?? '—' },
    {
      key: 'angkatan',
      header: 'Angkatan',
      render: (r) => <span className="tabular text-ink-secondary">{r.angkatan ?? '—'}</span>,
    },
    {
      key: 'joined',
      header: 'Bergabung',
      render: (r) => (
        <span className="tabular text-ink-secondary">
          {formatMonthYear(r.joinedPeriod)}
        </span>
      ),
    },
    { key: 'members', header: 'PELITA SLI', render: (r) => <span className="tabular">{r.memberCount}</span> },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <StatusBadge status={r.status} />,
      hideOnCard: true,
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Database TBM"
        description="Profil seluruh Taman Bacaan Masyarakat yang mengikuti program."
        action={
          user?.role === 'super_admin' && (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => navigate('/database/tbm/impor')}>
                ⭳ Impor
              </Button>
              <Button onClick={() => navigate('/database/tbm/baru')}>+ TBM Baru</Button>
            </div>
          )
        }
      />

      {banner && (
        <Banner tone={banner.startsWith('Gagal') ? 'danger' : 'success'}>{banner}</Banner>
      )}

      <Card padded={false}>
        <SelectionBar
          count={selected.size}
          label="TBM"
          onClear={() => setSelected(new Set())}
          onDelete={() => setConfirming(true)}
        />

        <FilterBar
          q={q}
          onQ={(v) => {
            setQ(v);
            setPage(1);
          }}
          placeholder="Cari nama atau kode TBM…"
          filters={[
            {
              label: 'Status',
              value: status,
              onChange: (v) => {
                setStatus(v);
                setPage(1);
              },
              options: [
                { value: '', label: 'Semua status' },
                { value: 'active', label: 'Aktif' },
                { value: 'inactive', label: 'Nonaktif' },
                { value: 'graduated', label: 'Lulus' },
              ],
            },
          ]}
        />

        {error ? (
          <div className="p-4">
            <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
          </div>
        ) : (
          <>
            <div className="px-2 pb-2 lg:px-0">
              <DataTable
                columns={columns}
                rows={data?.data ?? []}
                rowKey={(r) => r.id}
                loading={isLoading}
                onRowClick={(r) => navigate(`/database/tbm/${r.id}`)}
                selection={
                  canDelete ? { selected, onChange: setSelected } : undefined
                }
                cardTitle={(r) => r.name}
                cardSubtitle={(r) =>
                  `${r.code} · ${r.regionName ?? 'Wilayah belum diisi'}${r.angkatan ? ` · ${r.angkatan}` : ''}`
                }
                cardBadge={(r) => <StatusBadge status={r.status} />}
                empty={{
                  title: q || status ? 'Tidak ada TBM yang cocok' : 'Belum ada data TBM',
                  description:
                    q || status
                      ? 'Coba ubah kata kunci atau filter.'
                      : 'Mulai dengan mengimpor data dari spreadsheet yang sudah ada.',
                }}
              />
            </div>
            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <ConfirmDialog
        open={confirming}
        title={`Hapus ${ids.length} TBM?`}
        confirmLabel={deleteMutation.isPending ? 'Menghapus…' : 'Ya, hapus permanen'}
        loading={deleteMutation.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() => deleteMutation.mutate()}
      >
        {preview.isLoading ? (
          <p>Menghitung data yang akan ikut terhapus…</p>
        ) : (
          <>
            <ul className="mb-3 max-h-40 overflow-y-auto rounded-[var(--radius-input)] bg-app px-3.5 py-2.5 text-[14px]">
              {(preview.data?.tbm ?? []).map((t) => (
                <li key={t.id} className="truncate py-0.5">
                  <span className="tabular text-ink-muted">{t.code}</span> {t.name}
                </li>
              ))}
            </ul>

            {(preview.data?.individuals.length ?? 0) > 0 && (
              <p className="mb-3 rounded-[var(--radius-input)] bg-warn-soft px-3.5 py-2.5 text-[14px] text-[#9a6400]">
                <strong>{preview.data?.individuals.length} data PELITA SLI</strong> di dalamnya{' '}
                <strong>tidak ikut terhapus</strong>, tapi kehilangan TBM-nya dan perlu dipasangkan
                ulang — {preview.data?.individuals.slice(0, 3).map((i) => i.name).join(', ')}
                {(preview.data?.individuals.length ?? 0) > 3 &&
                  `, dan ${(preview.data?.individuals.length ?? 0) - 3} lainnya`}
                . Selama belum dipasangkan, assessor tidak bisa melihat mereka.
              </p>
            )}

            <p>
              Tindakan ini <strong>tidak bisa dibatalkan</strong>. Kalau TBM ini hanya berhenti
              aktif, ubah statusnya menjadi <em>Nonaktif</em> supaya datanya tetap tersimpan.
            </p>
          </>
        )}
      </ConfirmDialog>
    </div>
  );
}
